import type { Cell } from '../types';
import { getAllValidMoves, makeMove } from '../logic/board';
import { evaluatePosition, evaluateMove } from './evaluation';

interface TranspositionEntry {
  hash: string;
  depth: number;
  score: number;
  flag: 'exact' | 'lowerbound' | 'upperbound';
  bestMove: {row: number, col: number} | null;
  age: number;
}

export class TranspositionTable {
  private table = new Map<string, TranspositionEntry>();
  private maxSize = 50000; // Reduced from 100000 to prevent memory buildup
  private currentAge = 0;
  private clearThreshold = 0.9; // Clear when 90% full instead of 100%

  get(hash: string): TranspositionEntry | null {
    return this.table.get(hash) || null;
  }

  set(hash: string, entry: TranspositionEntry): void {
    entry.age = this.currentAge;
    
    // More aggressive clearing: clear when 90% full, remove 20% of oldest entries
    if (this.table.size >= this.maxSize * this.clearThreshold) {
      // Remove oldest entries
      const entries = Array.from(this.table.entries());
      entries.sort((a, b) => a[1].age - b[1].age);
      const toRemove = entries.slice(0, Math.floor(this.maxSize * 0.2)); // Remove 20% instead of 10%
      toRemove.forEach(([key]) => this.table.delete(key));
    }
    
    this.table.set(hash, entry);
  }

  clear(): void {
    this.table.clear();
    this.currentAge++;
  }

  /**
   * Clear old entries periodically to prevent memory buildup
   * Call this every N moves to keep memory usage reasonable
   */
  clearOldEntries(keepPercentage: number = 0.7): void {
    if (this.table.size < this.maxSize * 0.5) return; // Only clear if table is getting large
    
    const entries = Array.from(this.table.entries());
    entries.sort((a, b) => b[1].age - a[1].age); // Sort by age (newest first)
    const keepCount = Math.floor(entries.length * keepPercentage);
    const toKeep = entries.slice(0, keepCount);
    
    this.table.clear();
    toKeep.forEach(([key, value]) => {
      this.table.set(key, value);
    });
    
    this.currentAge++;
  }
}

// Minimax with Alpha-Beta Pruning
export const minimax = (
  board: (Cell | null)[][], 
  depth: number, 
  alpha: number, 
  beta: number, 
  isMaximizing: boolean, 
  playerColor: 'white' | 'black',
  transTable: TranspositionTable,
  quiescence: (board: (Cell | null)[][], alpha: number, beta: number, currentTurnColor: 'white' | 'black', evalColor: 'white' | 'black', isMaximizing: boolean, qDepth: number, maxQDepth: number) => { score: number }
): {score: number, bestMove: {row: number, col: number} | null} => {
  // Generate position hash for transposition table
  const positionHash = JSON.stringify(board);
  const ttEntry = transTable.get(positionHash);
  
  if (ttEntry && ttEntry.depth >= depth) {
    if (ttEntry.flag === 'exact') {
      return {score: ttEntry.score, bestMove: ttEntry.bestMove};
    } else if (ttEntry.flag === 'lowerbound' && ttEntry.score >= beta) {
      return {score: ttEntry.score, bestMove: ttEntry.bestMove};
    } else if (ttEntry.flag === 'upperbound' && ttEntry.score <= alpha) {
      return {score: ttEntry.score, bestMove: ttEntry.bestMove};
    }
  }
  
  // Base case -> use quiescence search to resolve tactical volatility
  if (depth === 0) {
    const currentColorAtYugo = isMaximizing ? playerColor : (playerColor === 'white' ? 'black' : 'white');
    const q = quiescence(board, alpha, beta, currentColorAtYugo, playerColor, isMaximizing, 0, 4);
    return {score: q.score, bestMove: null};
  }
  
  const currentColor = isMaximizing ? playerColor : (playerColor === 'white' ? 'black' : 'white');
  const moves = getAllValidMoves(board, currentColor);
  
  if (moves.length === 0) {
    const score = evaluatePosition(board, playerColor);
    return {score, bestMove: null};
  }
  
  // Move ordering - prioritize center moves and high-value squares
  moves.sort((a, b) => {
    const aScore = evaluateMove(board, a.row, a.col, currentColor, 'ai-3');
    const bScore = evaluateMove(board, b.row, b.col, currentColor, 'ai-3');
    return bScore - aScore;
  });
  
  let bestMove: {row: number, col: number} | null = null;
  let bestScore = isMaximizing ? -Infinity : Infinity;
  
  for (const move of moves) {
    const newBoard = makeMove(board, move.row, move.col, currentColor);
    const result = minimax(newBoard, depth - 1, alpha, beta, !isMaximizing, playerColor, transTable, quiescence);
    
    if (isMaximizing) {
      if (result.score > bestScore) {
        bestScore = result.score;
        bestMove = move;
      }
      alpha = Math.max(alpha, result.score);
    } else {
      if (result.score < bestScore) {
        bestScore = result.score;
        bestMove = move;
      }
      beta = Math.min(beta, result.score);
    }
    
    if (beta <= alpha) {
      break; // Alpha-beta pruning
    }
  }
  
  // Store in transposition table
  const flag = bestScore <= alpha ? 'upperbound' : bestScore >= beta ? 'lowerbound' : 'exact';
  transTable.set(positionHash, {
    hash: positionHash,
    depth,
    score: bestScore,
    flag,
    bestMove,
    age: 0
  });
  
  return {score: bestScore, bestMove};
};

